import MD5 from 'crypto-js/md5';
import { dynamicRequest, RequestOptions } from '../api-utils/api-utils';
import { InitializeCompareApiResponse } from '../../types/responses/InitializeCompareApi';
import { InitializeAcquisitionsAdobeModeApiResponse, InitializeCorporateAdobeModeApiResponse } from '../../types/responses/InitializeAdobeModeApi';
import SessionStorage from '../SessionStorage';
import { hexToBase64 } from '../cob-utils';
import { API_MAPPING } from '../api-utils/api-url-map';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { AcquisitionConfiguration } from '../../types/models/AcquisitionConfiguration';

interface ISignatureOptions extends RequestOptions {
    url: String;
    authToken: string;
}

const acquisitionConfigMetaPropValue = PathUtils.getMetaPropertyValue('AcquisitionsConfigurations');
const AcquisitionsConfig: AcquisitionConfiguration = acquisitionConfigMetaPropValue ?
    JSON.parse(acquisitionConfigMetaPropValue) : { apiHost: '', clientId: '' };

/**
 * Should only be called within an acquisition scope.
 * It is the developers responsibility to ensure this is called within the scope of acquisitions.
 */
export const CompareApi = {
    /**
     * Method to initialize the client to make API calls. Must be called before making any API calls.
     * Sets the initResponse SessionStorage variable for use in other API calls.
     * @see dynamicRequest
     */
    async initializeApi() {
        const clientId = AcquisitionsConfig.clientId ?? '';
        const currentTimeStamp = CompareApi.getCurrentTimestamp();

        const headers = {
            'Content-Type': 'application/json',
            'X-C1B-CLIENTID': clientId,
            'X-C1B-TIMESTAMP': currentTimeStamp,
        };

        try {
            const response = await dynamicRequest<InitializeCompareApiResponse>(
                'init-compare-api',
                {
                    headers: headers,
                    requestType: 'GET',
                });
            if (response.status === 200) {
                SessionStorage.set('initResponse', JSON.stringify(response.data));
            }
            return response;

        } catch (error) {
            console.error(error);
            throw new Error('Acquisition Initialization failed! Please try again.');
        }

    },

    /**
     * Method to initialize the client to make Adobe Mode Acquisitions API calls. Must be called before making any Acquisition API calls.
     * Sets the initResponse SessionStorage variable for use in other API calls.
     * @see dynamicRequest
     */
    async initializeAcquisitionsAdobeModeApi() {
        const clientId = AcquisitionsConfig.clientId ?? '';
        const currentTimeStamp = CompareApi.getCurrentTimestamp();

        const headers = {
            'Content-Type': 'application/json',
            'X-C1B-CLIENTID': clientId,
            'X-C1B-TIMESTAMP': currentTimeStamp,
        };

        try {
            const response = await dynamicRequest<InitializeAcquisitionsAdobeModeApiResponse>(
                'init-acquisitions-adobe-mode-api',
                {
                    headers: headers,
                    requestType: 'GET',
                },
            );
            if (response.status === 200) {
                response.data.success = true;
                SessionStorage.set('initResponse', JSON.stringify(response.data));
                SessionStorage.set('adobeMode', 'true');
                return;
            }
            throw response;
        } catch (err) {
            console.error(err);
            throw new Error('Acquisition Initialization failed! Please try again.');
        }
    },

    /**
     * Method to initialize the client to make Adobe Mode Corporate API calls. Must be called before making any Corporate API calls.
     * Sets the initResponse SessionStorage variable for use in other API calls.
     * @see dynamicRequest
     */
    async initializeCorporateAdobeModeApi() {
        const clientId = AcquisitionsConfig.clientId ?? '';
        const currentTimeStamp = CompareApi.getCurrentTimestamp();

        const headers = {
            'Content-Type': 'application/json',
            'X-C1B-CLIENTID': clientId,
            'X-C1B-TIMESTAMP': currentTimeStamp,
        };

        try {
            const response = await dynamicRequest<InitializeCorporateAdobeModeApiResponse>(
                'init-corporate-adobe-mode-api',
                {
                    headers: headers,
                    requestType: 'GET',
                },
            );
            if (response.status === 200) {
                SessionStorage.set('initResponse', JSON.stringify(response.data));
                SessionStorage.set('adobeMode', 'true');
                return;
            }
            throw response;
        } catch (err) {
            console.error(err);
            throw new Error('Acquisition Initialization failed! Please try again.');
        }
    },

    /**
     * Creates timestamp from SessionStorage values based on the time of the call.
     * For use in header arguments for API calls.
     */
    getCurrentTimestamp() {
        const date = new Date();
        const servTimeDiff = parseInt(SessionStorage.tryGet('C1B-Serv-Time-Diff', '0'));
        return (date.getTime() + servTimeDiff).toString();
    },
    /**
     * Creates a signature string based on API request variables.
     * For use in header arguments for API calls.
     * The functions under 'see also' are used used in this method.
     * @see formatHeaders
     * @see MD5
     * @see hexToBase64
     * @param options
     */
    getSignature(options: ISignatureOptions) {
        const formattedUrl = options.url.startsWith('http')
            ? document.location.protocol + '//' + document.location.host + options.url
            : options.url;

        const requestString =
            options.requestType ?? 'GET' +
            formattedUrl +
            formatHeaders(options.headers ?? {}) +
            JSON.stringify(options.body);

        const md5HashHex = MD5(requestString, { key: options.authToken });

        return hexToBase64(md5HashHex.toString());
    },
    /**
     * Retrieves the initResponse SessionStorage value
     * @see SessionStorage
     */
    getInitResponse() {
        if (!SessionStorage.verifyNotEmpty('initResponse')) {
            return null;
        }

        const initResponse = SessionStorage.get('initResponse');
        if (initResponse) {
            return JSON.parse(initResponse);
        }
    },
    /**
     * Retrieves the Authorization Token.
     * For use in header arguments for API calls.
     * @see getInitResponse
     */
    getAuthToken() {
        const initResponse = this.getInitResponse();
        if (initResponse) {
            return initResponse.Token;
        }
        return null;
    },
    /**
     * Retrieves the Session ID.
     * For use in header arguments for API calls.
     * @see getInitResponse
     */
    getSessionId() {
        const initResponse = this.getInitResponse();
        if (initResponse) {
            return initResponse.SessionId;
        }
        return null;
    },
    /**
     * Wrapper function for making Acquisition API calls.
     * initializeApi() must be called first.
     * <T> is the response type for the API call similar to dynamicRequest<T>().
     * @see initializeApi
     * @see dynamicRequest
     * @param requestName
     * @param options
     */
    async acquisitionApiCall<T>(requestName: keyof typeof API_MAPPING, options: RequestOptions) {
        const authToken = this.getAuthToken();
        const sessionId = this.getSessionId();
        const timeStamp = this.getCurrentTimestamp();

        const headers: Record<string, string> = {
            'X-C1B-CLIENTID': AcquisitionsConfig.clientId ?? '',
            'X-C1B-TIMESTAMP': timeStamp,
            'X-C1B-SID': sessionId,
            ...options.headers,
        };

        headers['X-C1B-SIGNATURE'] = this.getSignature({
            url: API_MAPPING[requestName].endpoint,
            requestType: options.requestType,
            headers: options.headers,
            body: options.body,
            authToken: authToken,
        });

        return await dynamicRequest<T>(
            requestName,
            {
                headers: headers,
                body: options.body,
                requestType: options.requestType,
                pathExtension: options.pathExtension,
                query: options.query,
            },
        );
    },
    /**
     * Clears the initResponse SessionStorage variable.
     * @see SessionStorage
     */
    removeCompareAPISessionStorageData() {
        SessionStorage.remove('initResponse');
    },
};

function formatHeaders(headers: Record<string, string>) {
    if (typeof headers === 'object') {
        const sortedHeaders: [string?] = [];
        Object.keys(headers)
            .sort()
            .forEach(function (key) {
                if (key.indexOf('X-C1B') >= 0) {
                    sortedHeaders.push(key + ':' + headers[key]);
                }
            });
        return sortedHeaders.join('&');
    }
    return headers;

}
